import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomDate } from '@app/_interfaces/generic.interface';
import { HabitNumericAnswer } from '@app/_interfaces/habit.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { toDate } from '@app/_services/date/date.functions';
import { SettingsService } from '@app/_services/settings.service';
import { getNumStrId } from '@app/_utils/utils';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { InputTimePickerComponent, InputTimePickerModel } from '@app/components/shared/inputs/input-time-picker/input-time-picker.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { Subject, takeUntil } from 'rxjs';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-habit-numeric-value',
  imports: [
    CommonModule,
    SvgComponent,
    InputTextComponent
  ],
  templateUrl: './habit-numeric-value.component.html',
  styleUrl: './habit-numeric-value.component.scss'
})

export class HabitNumericValueComponent {

  numericControl: FormControl = new FormControl(null, [Validators.required, Validators.maxLength(10)]);
  timestamp: FormControl = new FormControl(new Date());
  initialTimestamp: Date = new Date();
  unSubscribe = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<HabitNumericValueComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { mode: 'new' | 'edit', value: HabitNumericAnswer | null, unit: string },
    public cc: CacheService,
    private alertService: AlertService,
    public ss: SettingsService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {
    if (this.data.value) {
      this.numericControl.setValue(this.data.value.value ? String(this.data.value.value) : null);
      this.timestamp.setValue(this.data.value.timestamp);
      this.initialTimestamp = this.data.value.timestamp;
    };

    dialogRef.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  normalizeDate = (date: any): Date | null => {
    if (!date) return null;
    if (typeof date === 'object' && '_seconds' in date) return toDate(date);
    return new Date(date);
  };

  hasChanges(): boolean {
    const timestamp = this.normalizeDate(this.timestamp.value);
    const initialTimestamp = this.normalizeDate(this.initialTimestamp);

    const timestampChanged = timestamp?.getTime() !== initialTimestamp?.getTime();
    if (this.data.mode === 'new') {
      return this.numericControl.value !== null || timestampChanged;
    } else {
      return Number(this.numericControl.value) !== this.data.value?.value || timestampChanged;
    }
  }

  save() {
    const numericAnswer: HabitNumericAnswer = {
      id: this.data.value ? this.data.value.id : getNumStrId(),
      value: Number(this.numericControl.value),
      timestamp: this.timestamp.value
    };
    this.dialogRef.close({ type: this.data.mode === 'new' ? 'ADD' : 'EDIT', value: numericAnswer });
  }

  openTimePicker() {
    const currentDate: Date = this.timestamp.value || new Date();
    const customDate: CustomDate = this.dateToCustomDate(currentDate);

    const dialogRef = this.modalService.openModalLevel4(
      InputTimePickerComponent,
      new InputTimePickerModel(customDate, true, true, false, false),
      { disableClose: true }
    );

    this.modalService.getClosedObservable(dialogRef).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result && result.customDate) {
        // Convert CustomDate back to Date
        this.timestamp.setValue(new Date(result.customDate.dateTime));
        this.cdr.markForCheck();
      }
    });
  }

  private dateToCustomDate(date: Date): CustomDate {
    date = this.normalizeDate(date) as Date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    const dateString = `${year}-${month}-${day}`;
    const timeString = `${hours}:${minutes}`;

    const tzOffsetMinutes = -date.getTimezoneOffset();
    const sign = tzOffsetMinutes >= 0 ? '+' : '-';
    const tzHours = String(Math.floor(Math.abs(tzOffsetMinutes) / 60)).padStart(2, '0');
    const tzMins = String(Math.abs(tzOffsetMinutes) % 60).padStart(2, '0');
    const timeWithOffset = `${dateString}T${timeString}:00.000${sign}${tzHours}:${tzMins}`;

    return {
      dateString,
      timeString,
      dateTime: date,
      timeWithOffset
    };
  }

  async closeDialog() {
    if (this.hasChanges()) {
      const res = await this.alertService.confirm(this.ss.texts()['saveChanges_title'], this.ss.texts()['common_discardContent'], this.ss.texts()['common_save'], this.ss.texts()['common_discard'], 'color-35', 'color-11');
      if (!res) return;
      if (res.confirm === true) {
        this.save();
      } else if (res.confirm === false) {
        this.dialogRef.close();
      }
    } else {
      this.dialogRef.close();
    }
  }
}
